// SuccessStories.jsx
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Slider from './Slider';
import { fetchSuccessStories } from '../data/successStoriesSlice';
import { useBackendTranslation } from '../utils/useBackendTranslation';
import BackendText from './BackendText';

const SuccessStories = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  
  // Get data from Redux store
  const { stories, title, status } = useSelector(state => state.successStories);
  
  // Use our custom hook to translate backend data
  const { translatedData: translatedStories, isLoading: isTranslating } = useBackendTranslation(stories);
  const { translatedData: translatedTitle } = useBackendTranslation(title);
  
  // Fetch stories on component mount
  useEffect(() => {
    // Always fetch stories when component mounts to ensure we have the latest data
    dispatch(fetchSuccessStories());
  }, [dispatch]);

  return (
    <section className="bg-gradient-to-br from-gray-50 to-white py-12 lg:py-16">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-gray-900 leading-tight">
            {/* Use BackendHeading for translated title from backend */}
            {status === 'loading' ? (
              t('loading')
            ) : (
              <BackendText 
                text={translatedTitle} 
                i18nKey="success_stories.title"
                fallback="Success Stories"
              />
            )}
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto mb-6">
            <BackendText
              i18nKey="success_stories.description"
              fallback="Real stories of transformation and hope from the laborers we support"
            />
          </p>
          <div className="w-24 h-1 bg-green-600 mx-auto"></div>
        </div>

        {/* Loading State */}
        {status === 'loading' || isTranslating || !translatedStories || translatedStories.length === 0 ? (
          <div className="text-center py-12">
            <div className="animate-pulse">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[1, 2, 3].map((item) => (
                  <div key={item} className="bg-gray-200 h-80 rounded-xl"></div>
                ))}
              </div>
            </div>
            <p className="text-gray-500 mt-6">{t('success_stories.loading')}</p>
          </div>
        ) : (
          <div className="success-stories-section">
            <Slider 
              data={translatedStories} 
              title={translatedTitle || ''}
              className="success-stories-slider"
              variant="stories"
            />
          </div>
        )}
      </div>
    </section>
  );
};

export default SuccessStories;